import {
  ListFilterIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  XIcon,
  type LucideIcon,
} from "lucide-react"
import { useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { Badge } from "@/primitives/badge.tsx"
import { Button } from "@/primitives/button.tsx"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/primitives/input-group.tsx"
import { cn } from "@/primitives/utils"
import { useShortcut, useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { chipValuesOf, isOffered, operatorOf } from "./filter-bar.ts"
import {
  DisplayMenu,
  FilterChip,
  FilterMenu,
  SearchField,
  type DisplayRow,
  type Facet,
} from "./filter-bar.tsx"
import { Updating, useLadder, useRegistered } from "./page-chrome.tsx"
import {
  directionOf,
  directions,
  filterSummaryOf,
  inForceOf,
  offersView,
  searchStandsAlone,
} from "./page-toolbar.ts"
import type { Sort } from "./table-view.ts"
import { useViewport } from "./viewport.tsx"
import { ViewSheet, type ViewSheetAt } from "./view-sheet.tsx"

/*
 * The bar under the PageHeader when the page filters a list, its controls given as data so each
 * layout draws them its own way. On the desk: Filter, its chips and the search at the start; the
 * count, the toggles and Display at the end. On `compact`: Filter, a search icon that opens a
 * full-width field over the row, and View, which opens the View sheet (`view-sheet.tsx`) holding
 * the facets, the order, Display, the toggles and the count; the chips go on a row of their own
 * under the bar, which scrolls sideways and away with the page.
 */

/** The list's search: what it says while empty, what is typed, and a way to hear each change. */
export type ToolbarSearch = {
  placeholder: string
  value: string
  onChange: (value: string) => void
}

/**
 * The list's order, as one choice among `options`: on the desk the Display menu's first row, on
 * `compact` a row of the View sheet that drills in to them. `label` names it, "Order by" unless the
 * page says what it orders ("Order tickets by"). `direction`, which way the order in force runs, is
 * offered under the options on `compact` alone, where no column head is there to reverse it.
 */
export type OrderBy = {
  label?: string
  value: string
  options: readonly { value: string; label: string }[]
  onChange: (value: string) => void
  direction?: {
    value: Sort["direction"]
    onChange: (direction: Sort["direction"]) => void
  }
}

/**
 * The view's other options, behind Display on the desk and in the View sheet on `compact`.
 * `nonDefault` puts a dot on the button that opens them, as the view isn't the default.
 */
export type ToolbarDisplay = { rows: readonly DisplayRow[]; nonDefault: boolean }

/**
 * Something that acts on the whole view at once, such as the Board's Collapse all: a ghost button
 * on the desk, a row of the View sheet on `compact`. Its label says what it would do now.
 */
export type ToolbarToggle = { label: string; icon: LucideIcon; onSelect: () => void }

/**
 * A chip for a view choice that narrows the list rather than a facet, such as My tickets' "Closed
 * included": after the facets' chips, its × taking the choice back.
 */
export type ViewChip = { label: string; onRemove: () => void }

/**
 * The PageToolbar, 40px tall and stuck under the PageHeader (under its tab row too on `compact`).
 * `facets` and `onClearFilters` are the Filter; `chips` the view choices in force beside its chips;
 * `search` the list's search; `count` the words for how many rows show ("12 tickets"); `toggles`,
 * `orderBy` and `display` the view's options. While `pending` (a refetch keeping the old rows), a
 * spinner leads the end. With none of them it is the toolbar's place alone, which a page loading
 * keeps so its list doesn't jump down as it arrives.
 */
export function PageToolbar(props: {
  facets?: readonly Facet[]
  onClearFilters?: () => void
  chips?: readonly ViewChip[]
  search?: ToolbarSearch
  count?: string
  toggles?: readonly ToolbarToggle[]
  orderBy?: OrderBy
  display?: ToolbarDisplay
  pending?: boolean
}) {
  useRegistered("toolbar")
  const { compact } = useViewport()
  return compact ? <CompactToolbar {...props} /> : <DeskToolbar {...props} />
}

type ToolbarProps = Parameters<typeof PageToolbar>[0]

function DeskToolbar({
  facets,
  onClearFilters = () => undefined,
  chips = [],
  search,
  count,
  toggles = [],
  orderBy,
  display,
  pending = false,
}: ToolbarProps) {
  const { underHeader } = useLadder()
  const start = facets !== undefined || chips.length > 0 || search !== undefined
  return (
    <div
      role="toolbar"
      className={cn(
        "sticky z-20 flex h-10 shrink-0 items-center gap-2 border-b border-border-subtle bg-background px-6",
        underHeader,
      )}
    >
      {start && (
        // The start fills to the end's controls, whatever its chips, so the chips it has room
        // for are measured against a width that never follows them (`FilterMenu`).
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {facets && <FilterMenu facets={facets} onClear={onClearFilters} />}
          {chips.map(({ label, onRemove }) => (
            <FilterChip key={label} values={[{ label }]} onRemove={onRemove} />
          ))}
          {search && (
            <SearchField
              placeholder={search.placeholder}
              value={search.value}
              onChange={search.onChange}
            />
          )}
        </div>
      )}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {pending && <Updating />}
        {count !== undefined && <Count count={count} />}
        {toggles.map(({ label, icon: Icon, onSelect }) => (
          <Button key={label} variant="ghost" size="sm" onClick={onSelect}>
            <Icon data-icon="inline-start" />
            {label}
          </Button>
        ))}
        <DeskDisplay orderBy={orderBy} display={display} />
      </div>
    </div>
  )
}

/** How many rows show, in words. */
export function Count({ count }: { count: string }) {
  return (
    <span className="text-xs whitespace-nowrap text-subtle-foreground tabular-nums">{count}</span>
  )
}

/**
 * The desk's Display menu, the order its first row, when the view has options. A card's table keeps
 * it on `compact`, where it adds the order's Direction under it, as there are no column heads there
 * to reverse it by.
 */
export function DeskDisplay({
  orderBy,
  display,
}: {
  orderBy: OrderBy | undefined
  display: ToolbarDisplay | undefined
}) {
  const { compact } = useViewport()
  if (!orderBy && !display) return null
  const direction = compact ? orderBy?.direction : undefined
  const rows: DisplayRow[] = [
    ...(orderBy
      ? [
          {
            label: orderBy.label ?? "Order by",
            control: {
              kind: "select" as const,
              value: orderBy.value,
              options: orderBy.options,
              onChange: orderBy.onChange,
            },
          },
        ]
      : []),
    ...(direction
      ? [
          {
            label: "Direction",
            control: {
              kind: "select" as const,
              value: direction.value,
              options: directions,
              onChange: (value: string) => {
                const chosen = directionOf(value)
                if (chosen) direction.onChange(chosen)
              },
            },
          },
        ]
      : []),
    ...(display?.rows ?? []),
  ]
  return <DisplayMenu rows={rows} nonDefault={display?.nonDefault ?? false} />
}

/**
 * The compact toolbar: Filter, counting the filters in force; a search icon, which opens the search
 * over the row with Cancel beside it; and View, at the header's 16px inset. The chips follow on a
 * row of their own, each reopening its facet in the sheet. A list with no options to view beyond
 * its filters shows its count in place of View, Filter opening the sheet alone. The desk's keys
 * still work in a narrow window: F opens the facets, `/` the search, Esc clears the filters.
 *
 * The field covers the row only while it is being edited. Left with a search in force (or arriving
 * with one in the URL), the row comes back with the search icon showing the term, so Filter and View
 * can still narrow what it found; Cancel alone takes the search back.
 */
function CompactToolbar({
  facets = [],
  onClearFilters,
  chips = [],
  search,
  count,
  toggles = [],
  orderBy,
  display,
  pending = false,
}: ToolbarProps) {
  const { underHeader } = useLadder()
  const [editing, setEditing] = useState(false)
  const field = useRef<HTMLInputElement>(null)
  const row = useRef<HTMLDivElement>(null)
  // Whether the field selects its term as it opens: it does for `/`, as on the desk, while a tap
  // leaves the caret at the term's end.
  const selects = useRef(false)
  const [sheet, setSheet] = useState<{ open: boolean; at: ViewSheetAt }>({
    open: false,
    at: { opened: "view", level: { at: "view" } },
  })
  const offered = facets.filter(isOffered)
  const chosen = facets.filter((facet) => facet.chosen.length > 0)
  const inForce = inForceOf(facets, chips)
  const viewed = offersView({ orderBy, display, toggles })
  const openAt = (at: ViewSheetAt) => setSheet({ open: true, at })
  const openFilter = () => openAt({ opened: "filter", level: { at: "filter" } })

  useShortcut("open-filter", openFilter, { enabled: offered.length > 0 })
  useShortcuts({ "clear-filters": () => onClearFilters?.() }, { enabled: chosen.length > 0 })
  const edit = (select: boolean) => {
    selects.current = select
    if (field.current) focusField(field.current, select)
    else setEditing(true)
  }
  useShortcut("search", () => edit(true), { enabled: search !== undefined })
  useLayoutEffect(() => {
    if (editing && field.current) focusField(field.current, selects.current)
  }, [editing])

  const open = search !== undefined && editing
  const close = () => {
    search?.onChange("")
    setEditing(false)
  }

  return (
    <>
      <div
        ref={row}
        role="toolbar"
        className={cn(
          "sticky z-20 flex h-10 shrink-0 items-center gap-1 border-b border-border-subtle bg-background px-2",
          underHeader,
        )}
      >
        {open ? (
          <>
            {/* The 40px row holds a 36px field, as tall as its buttons. On touch the label around
                it reaches 4px past the field, so a tap just above or below lands on the label and
                so in the field, while the label's reach stays under the field and its × button.
                The chrome above the row clips that reach at the row's top edge, so the field's
                target is the row's whole height and 2px below it: as much as it can have without
                a taller row. */}
            <label className="isolate min-w-0 flex-1 touch-target after:-z-10">
              <InputGroup className="h-8 touch:min-h-9">
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
                <InputGroupInput
                  ref={field}
                  type="search"
                  // The field's own × clears it; the browser's would be a second one.
                  className="[&::-webkit-search-cancel-button]:appearance-none"
                  enterKeyHint="search"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  aria-label={search.placeholder}
                  placeholder={search.placeholder}
                  value={search.value}
                  onChange={(event) => search.onChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") event.currentTarget.blur()
                    if (event.key !== "Escape") return
                    if (search.value) search.onChange("")
                    else close()
                  }}
                  // Focus leaving the row folds the field back into it, the term kept; focus
                  // moving to its × or Cancel doesn't.
                  onBlur={(event) => {
                    if (!row.current?.contains(event.relatedTarget)) setEditing(false)
                  }}
                />
                {search.value && (
                  <InputGroupAddon align="inline-end">
                    <InputGroupButton
                      size="icon-xs"
                      aria-label="Clear search"
                      onMouseDown={keepFocus}
                      onClick={() => {
                        search.onChange("")
                        field.current?.focus()
                      }}
                    >
                      <XIcon />
                    </InputGroupButton>
                  </InputGroupAddon>
                )}
              </InputGroup>
            </label>
            <Button variant="ghost" size="sm" onMouseDown={keepFocus} onClick={close}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            {offered.length > 0 && (
              <Button variant="ghost" size="sm" aria-haspopup="dialog" onClick={openFilter}>
                <ListFilterIcon data-icon="inline-start" />
                Filter
                {inForce > 0 && <Badge>{inForce}</Badge>}
              </Button>
            )}
            {search &&
              (search.value ? (
                <Button
                  variant="secondary"
                  size="sm"
                  aria-label={`${search.placeholder}: ${search.value}`}
                  className="min-w-0 shrink"
                  onClick={() => edit(false)}
                >
                  <SearchIcon data-icon="inline-start" />
                  <span className="truncate">{search.value}</span>
                </Button>
              ) : searchStandsAlone({ filters: offered.length, viewed, count }) ? (
                // Nothing else in the row: the search reads as the field it opens.
                <Button
                  variant="ghost"
                  size="sm"
                  className="min-w-0 flex-1 justify-start font-normal text-muted-foreground"
                  onClick={() => edit(false)}
                >
                  <SearchIcon data-icon="inline-start" />
                  <span className="truncate">{search.placeholder}</span>
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={search.placeholder}
                  onClick={() => edit(false)}
                >
                  <SearchIcon />
                </Button>
              ))}
            <div className="ml-auto flex shrink-0 items-center gap-2 pr-2">
              {pending && <Updating />}
              {viewed ? (
                <Button
                  variant="outline"
                  size="sm"
                  aria-haspopup="dialog"
                  className="relative"
                  onClick={() => openAt({ opened: "view", level: { at: "view" } })}
                >
                  <SlidersHorizontalIcon data-icon="inline-start" />
                  View
                  {display?.nonDefault && (
                    <span
                      aria-label="Changed from the default"
                      className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-primary"
                    />
                  )}
                </Button>
              ) : (
                count !== undefined && <Count count={count} />
              )}
            </div>
          </>
        )}
      </div>
      {inForce > 0 && (
        <ChipRow>
          {chosen.map((facet) => (
            <FilterChip
              key={facet.id}
              field={{ label: facet.label, icon: facet.icon }}
              operator={operatorOf(facet.mode, facet.chosen.length)}
              values={chipValuesOf(facet.values, facet.chosen, facet.labelOf)}
              onRemove={() => facet.onChange([])}
              {...(offered.includes(facet) && {
                onOpen: () => openAt({ opened: "filter", level: { at: "facet", id: facet.id } }),
              })}
            />
          ))}
          {chips.map(({ label, onRemove }) => (
            <FilterChip key={label} values={[{ label }]} onRemove={onRemove} />
          ))}
          {onClearFilters && (
            <Button variant="ghost" size="xs" onClick={onClearFilters}>
              Clear
            </Button>
          )}
        </ChipRow>
      )}
      {(viewed || offered.length > 0) && (
        <ViewSheet
          open={sheet.open}
          at={sheet.at}
          onOpenChange={(next) => setSheet((now) => ({ ...now, open: next }))}
          facets={offered}
          chips={chips}
          onClearFilters={onClearFilters}
          inForce={inForce}
          filterSummary={filterSummaryOf(facets, chips)}
          orderBy={orderBy}
          display={display}
          toggles={toggles}
          count={count}
        />
      )}
    </>
  )
}

/** Puts the caret in the search, selecting its term (`select`) or after it. */
function focusField(field: HTMLInputElement, select: boolean) {
  field.focus()
  if (select) field.select()
  else field.setSelectionRange(field.value.length, field.value.length)
}

/**
 * Keeps focus in the search through a press on a button beside it, which would otherwise blur the
 * field and fold it away before the press lands.
 */
function keepFocus(event: { preventDefault: () => void }) {
  event.preventDefault()
}

/** The compact toolbar's chips: a row under it that scrolls sideways, then away with the page. */
function ChipRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 items-center gap-2 overflow-x-auto overscroll-x-contain border-b border-border-subtle px-4 py-2 no-scrollbar">
      {children}
    </div>
  )
}
